import { isTempClipId } from '@src/services/WriteQueueService';

import type { AssociatedVideo, VideoClip } from '@src/types.ts/story';

/**
 * Build the public "live/unlisted" clip page URL for a video, optionally pinned
 * to an aspect ratio and a language. Returns '' when the id is missing or not
 * yet persisted (a temp clip id), so callers can gate on a falsy result. Shared
 * by the clip share modal and the Downloads tab so both agree on the URL shape
 * + the not-yet-persisted guard.
 *
 * PLAT-11485: `language` is the publish record's BCP-47 code. Every language of
 * one aspect ratio renders into the *same* Video record and only the canonical
 * (original-language) cell mirrors to `videoFilePrimary`, so without this param
 * a translated row's link is byte-identical to the original's and the share page
 * has no way to know which render was meant. Omitted when absent, keeping the
 * original-language URL unchanged.
 */
export function buildClipLiveLink(
  videoId: string | null | undefined,
  aspectRatio?: string | null,
  language?: string | null,
): string {
  if (!videoId || isTempClipId(videoId)) return '';
  const params = new URLSearchParams();
  if (aspectRatio) params.set('aspectRatio', aspectRatio);
  if (language) params.set('language', language);
  const query = params.toString();
  return `${process.env.REACT_APP_API_URL}/clips/v/${videoId}${
    query ? `?${query}` : ''
  }`;
}

/**
 * PLAT-11485: the `language` to put on a share link for one publish record, or
 * `null` for the story's original language. The original language already lives
 * in the Video record's `videoFilePrimary` (and is the only variant Mux has
 * processed), so its links stay bare — unchanged from before the language param
 * existed. `|| 'en'` mirrors the same default `PublishFanoutService.expandCells`
 * uses when stamping `language` onto a record, so the two can't disagree.
 */
export function shareLanguageForRecord(
  recordLanguage: string,
  originalVideoLanguage: string | null | undefined,
): string | null {
  const originalLang = originalVideoLanguage || 'en';
  return recordLanguage === originalLang ? null : recordLanguage;
}

/**
 * Resolve the clip (or per-AR variant) behind a published video id from the
 * story's clip graph, so the Share Live Link modal can anchor its download
 * toggle on the full `VideoClip` (fan-out needs `associatedVideos` /
 * `_allReferencingVideos`). Falls back to a bare `{ id }` when the story graph
 * doesn't carry the video (the link still works; the toggle then only updates
 * that one record).
 */
export function findClipForVideoId(
  otherVideos: VideoClip[] | undefined,
  videoId: string,
): { clip: VideoClip | AssociatedVideo; mainClip?: VideoClip } {
  for (const clip of otherVideos ?? []) {
    if (clip.id === videoId) return { clip, mainClip: clip };
    const variant = clip.associatedVideos?.find(v => v.id === videoId);
    if (variant) return { clip: variant, mainClip: clip };
  }
  return { clip: { id: videoId } as AssociatedVideo };
}
